import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Select, Input, Button, Tag, Upload,
  Space, Typography, message, Spin, theme, Collapse, Switch,
} from 'antd';
import {
  SendOutlined, UploadOutlined, CopyOutlined, CheckOutlined,
  HistoryOutlined, FileTextOutlined, PictureOutlined,
} from '@ant-design/icons';
import { useList } from '@refinedev/core';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';
import { parseSections, joinSections, extractVariables, PromptSections } from '../prompts/prompt-parser';
import { resizeImageForLlm } from '../utils/image-resize';


const tagStyle = (color: string) => ({
  borderColor: color, color, background: `${color}15`,
  borderRadius: 9999, padding: '2px 12px', fontWeight: 500,
});

function formatResponse(result: any): string {
  if (!result) return 'No response data';

  // If it's a string, try parse then pretty-print
  if (typeof result === 'string') {
    try {
      return JSON.stringify(JSON.parse(result), null, 2);
    } catch {
      return result;
    }
  }

  // If it's an array of LlmAnalysisResult
  if (Array.isArray(result)) {
    // Simple text response — just show description text
    if (result.length === 1 && result[0].name === 'response') {
      const desc = result[0].description;
      try { return JSON.stringify(JSON.parse(desc), null, 2); }
      catch { return desc; }
    }
    // Structured results — pretty JSON
    return JSON.stringify(result, null, 2);
  }

  return JSON.stringify(result, null, 2);
}

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button size="small" type="text"
      icon={copied ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
      onClick={async () => { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
    />
  );
};

interface ProviderModel {
  providerId: string;
  providerName: string;
  providerType: string;
  modelSlug: string;
  modelName: string;
}

interface RunResult {
  providerId: string;
  providerName: string;
  providerType: string;
  model: string;
  status: string;
  result?: any;
  parsedResult?: any;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  latencyMs: number;
  error?: string;
}

interface HistoryItem {
  id: string;
  promptSlug: string;
  providerModelKeys: string[];
  inputType: string;
  textInput?: string;
  createdAt: string;
}

const relativeTime = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

// ─── Column Result Card ───

const ColumnCard: React.FC<{ run: RunResult }> = ({ run }) => {
  const isSuccess = run.status === 'success';
  const raw = run.parsedResult || run.result || run.error;
  const formatted = formatResponse(raw);
  return (
    <Card
      size="small"
      style={{
        flex: '1 1 0', minWidth: 0,
        borderLeft: `3px solid ${isSuccess ? '#52c41a' : '#ff4d4f'}`,
        background: isSuccess ? undefined : '#fff2f0',
      }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space size={4}>
            <Tag style={tagStyle(isSuccess ? '#52c41a' : '#ff4d4f')}>{run.status}</Tag>
            <Typography.Text strong style={{ fontSize: 13 }}>{run.providerName}</Typography.Text>
          </Space>
          <CopyButton text={formatted} />
        </div>
      }
    >
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{run.model}</Typography.Text>
      </div>
      <Space size={16} style={{ marginBottom: 8 }}>
        <Typography.Text style={{ fontSize: 12 }}>
          <strong>{run.latencyMs}ms</strong>
        </Typography.Text>
        {run.inputTokens != null && <Typography.Text type="secondary" style={{ fontSize: 12 }}>In: {run.inputTokens}</Typography.Text>}
        {run.outputTokens != null && <Typography.Text type="secondary" style={{ fontSize: 12 }}>Out: {run.outputTokens}</Typography.Text>}
      </Space>
      <pre style={{
        background: '#1e1e2e', color: '#cdd6f4', padding: 16, borderRadius: 8,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.6,
        maxHeight: 400, overflow: 'auto', margin: 0,
        fontFamily: "'SF Mono', 'Fira Code', 'JetBrains Mono', monospace",
      }}>
        <code>{formatted}</code>
      </pre>
    </Card>
  );
};

// ─── Main Component ───

export const LlmPlayground: React.FC = () => {
  const { token } = theme.useToken();

  const { data: promptsData } = useList({ resource: 'llm-prompts', pagination: { pageSize: 100 } });
  const prompts = promptsData?.data || [];

  const { data: providersData } = useList({ resource: 'llm-providers', pagination: { pageSize: 100 } });
  const providers = (providersData?.data || []).filter((p: any) => p.isActive);

  // Prompt state
  const [selectedPromptId, setSelectedPromptId] = useState<string | undefined>(undefined);
  const [promptLoading, setPromptLoading] = useState(false);
  const [sections, setSections] = useState<PromptSections>({ system: '', user: '', output: '' });
  const [promptSlug, setPromptSlug] = useState<string>('');

  // Variables
  const [testVars, setTestVars] = useState<Record<string, string>>({});

  // Provider + model selection
  const [selectedModelKeys, setSelectedModelKeys] = useState<string[]>([]);

  // Input
  const [inputType, setInputType] = useState<'text' | 'image'>('text');
  const [textInput, setTextInput] = useState('');
  const [imageBase64, setImageBase64] = useState<string | undefined>(undefined);

  // Run
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<RunResult[]>([]);
  const [enableThinking, setEnableThinking] = useState<boolean>(false);

  // History
  const [historyRuns, setHistoryRuns] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  // Provider+model combos from provider's modelList
  const providerModels: ProviderModel[] = useMemo(() => {
    const combos: ProviderModel[] = [];
    providers.forEach((p: any) => {
      const models: string[] = p.modelList || [p.defaultModel];
      models.forEach((model: string) => {
        combos.push({ providerId: p.id, providerName: p.name, providerType: p.type, modelSlug: model, modelName: model });
      });
    });
    return combos;
  }, [providers]);

  const detectedVars = useMemo(() => extractVariables(joinSections(sections)), [sections]);

  // Load history
  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    axiosInstance.get(`${API_BASE_URL}/admin/llm/playground-runs?limit=50`)
      .then((resp) => {
        const items = resp.data?.items || resp.data?.data || resp.data || [];
        setHistoryRuns(items);
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Load prompt content
  useEffect(() => {
    if (!selectedPromptId) { setSections({ system: '', user: '', output: '' }); setPromptSlug(''); return; }
    const p = prompts.find((pr: any) => pr.id === selectedPromptId);
    if (p) setPromptSlug(p.slug);
    setPromptLoading(true);
    axiosInstance.get(`${API_BASE_URL}/admin/llm/prompts/${selectedPromptId}`)
      .then((resp) => {
        const versions = resp.data.versions || [];
        const active = versions.find((v: any) => v.isActive) || versions[0];
        if (active) {
          setSections(parseSections(active.content));
          if (active.config?.enableThinking != null) setEnableThinking(active.config.enableThinking);
        }
      })
      .catch(() => message.error('Failed to load prompt'))
      .finally(() => setPromptLoading(false));
  }, [selectedPromptId]);

  // Auto-detect variables
  useEffect(() => {
    const vars: Record<string, string> = {};
    detectedVars.forEach((v) => { vars[v] = testVars[v] || ''; });
    setTestVars(vars);
  }, [detectedVars.length]);

  const handleImageUpload = useCallback(async (file: File) => {
    try {
      const resized = await resizeImageForLlm(file);
      setImageBase64(resized.base64);
      message.info(`Resized: ${(file.size / 1024).toFixed(0)}KB → ${(resized.resizedSize / 1024).toFixed(0)}KB`);
    } catch { message.error('Image resize failed'); }
    return false;
  }, []);

  // Load history run as template
  const loadHistoryRun = async (runId: string) => {
    try {
      const resp = await axiosInstance.get(`${API_BASE_URL}/admin/llm/playground-runs/${runId}`);
      const run = resp.data;
      if (!run) return;

      setSelectedHistoryId(runId);
      setPromptSlug(run.promptSlug || '');

      // Set prompt if matching
      const matchingPrompt = prompts.find((p: any) => p.slug === run.promptSlug);
      if (matchingPrompt) setSelectedPromptId(String(matchingPrompt.id));

      // Set sections
      if (run.systemPromptOverride) {
        setSections(parseSections(run.systemPromptOverride));
      }

      // Set variables
      if (run.promptVariables) setTestVars(run.promptVariables as Record<string, string>);

      // Set model selection
      if (run.providerModelKeys) setSelectedModelKeys(run.providerModelKeys as string[]);

      // Set input
      setInputType(run.inputType === 'image' ? 'image' : 'text');
      setTextInput(run.textInput || '');

      // Set results
      if (run.results) setResults(run.results as RunResult[]);

      message.success('Run loaded');
    } catch { message.error('Failed to load run'); }
  };

  const handleRun = async () => {
    if (!promptSlug) { message.warning('Select a prompt first'); return; }
    if (selectedModelKeys.length === 0) { message.warning('Select at least one model'); return; }

    setRunning(true);
    setResults([]);

    const providerIds: string[] = [];
    const models: string[] = [];
    selectedModelKeys.forEach((key) => {
      const pm = providerModels.find((p) => `${p.providerId}:${p.modelSlug}` === key);
      if (pm) {
        providerIds.push(pm.providerId);
        models.push(pm.modelSlug);
      }
    });

    const payload: any = {
      promptSlug, promptVariables: testVars, providerIds, models,
      systemPromptOverride: joinSections(sections),
      enableThinking,
    };
    if (inputType === 'text' && textInput.trim()) payload.text = textInput;
    else if (inputType === 'image' && imageBase64) { payload.imageBase64 = imageBase64; payload.imageMimeType = 'image/jpeg'; }

    try {
      const response = await axiosInstance.post(`${API_BASE_URL}/llm/playground`, payload);
      setResults(response.data?.runs || []);
      if (response.data?.runId) {
        setSelectedHistoryId(response.data.runId);
        loadHistory();
      }
      message.success(`Completed ${response.data?.runs?.length || 0} runs`);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Playground run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 16, padding: '0 0 24px' }}>
      {/* History Sidebar */}
      {!historyCollapsed && (
        <div style={{ width: 280, flexShrink: 0 }}>
          <Card
            title={<Space><HistoryOutlined /> History</Space>}
            size="small"
            extra={<Button size="small" type="text" onClick={() => setHistoryCollapsed(true)}>Hide</Button>}
            bodyStyle={{ padding: 0 }}
          >
            {historyLoading ? <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div> :
              historyRuns.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: token.colorTextSecondary, fontSize: 12 }}>No runs yet</div>
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                  {historyRuns.map((run) => (
                    <div
                      key={run.id}
                      onClick={() => loadHistoryRun(run.id)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        background: selectedHistoryId === run.id ? `${token.colorPrimary}10` : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography.Text strong style={{ fontSize: 12 }}>{run.promptSlug}</Typography.Text>
                        <Space size={4}>
                          {run.inputType === 'image' ? <PictureOutlined style={{ fontSize: 11, color: token.colorTextSecondary }} />
                            : <FileTextOutlined style={{ fontSize: 11, color: token.colorTextSecondary }} />}
                          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                            {(run.providerModelKeys as string[])?.length || 0} models
                          </Typography.Text>
                        </Space>
                      </div>
                      {run.textInput && (
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                          {run.textInput.length > 50 ? `${run.textInput.slice(0, 50)}...` : run.textInput}
                        </Typography.Text>
                      )}
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>{relativeTime(run.createdAt)}</Typography.Text>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>LLM Playground</Typography.Title>
          {historyCollapsed && (
            <Button icon={<HistoryOutlined />} onClick={() => setHistoryCollapsed(false)}>History</Button>
          )}
        </div>

        {/* Prompt */}
        <Card title="Prompt" size="small" style={{ marginBottom: 16 }}>
          <Select
            placeholder="Select a prompt to load..."
            style={{ width: '100%', marginBottom: 12 }}
            showSearch optionFilterProp="children"
            onChange={(value) => setSelectedPromptId(value)}
            options={prompts.map((p: any) => ({ label: `${p.name} — ${p.slug}`, value: p.id }))}
          />
          {promptLoading ? <Spin /> : selectedPromptId ? (
            <>
              <Collapse size="small" defaultActiveKey={['system']} items={[
                { key: 'system', label: 'System Prompt', children: (
                  <Input.TextArea value={sections.system} onChange={(e) => setSections((s) => ({ ...s, system: e.target.value }))}
                    rows={6} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }} placeholder="System instructions..." />
                )},
                { key: 'user', label: 'User Prompt', children: (
                  <Input.TextArea value={sections.user} onChange={(e) => setSections((s) => ({ ...s, user: e.target.value }))}
                    rows={3} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }} placeholder="User template with {{variables}}..." />
                )},
                { key: 'output', label: 'Output Structure', children: (
                  <Input.TextArea value={sections.output} onChange={(e) => setSections((s) => ({ ...s, output: e.target.value }))}
                    rows={4} style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }} placeholder="Expected JSON schema..." />
                )},
              ]} />
              {detectedVars.length > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                  Variables: {detectedVars.map((v) => `{{${v}}}`).join(', ')}
                </Typography.Text>
              )}
            </>
          ) : (
            <Typography.Text type="secondary">Select a prompt to load and edit</Typography.Text>
          )}
        </Card>

        {/* Config */}
        <Card title="Configuration" size="small" style={{ marginBottom: 16 }}>
          {detectedVars.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Variables</Typography.Text>
              {detectedVars.map((v) => (
                <Input key={v} placeholder={`{{${v}}}`} value={testVars[v] || ''}
                  onChange={(e) => setTestVars((prev) => ({ ...prev, [v]: e.target.value }))}
                  addonBefore={v} style={{ marginBottom: 8 }} />
              ))}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Providers & Models</Typography.Text>
            {providerModels.length === 0 ? (
              <Typography.Text type="secondary">No active providers found.</Typography.Text>
            ) : (
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Search and select models..."
                value={selectedModelKeys}
                onChange={setSelectedModelKeys}
                showSearch
                optionFilterProp="label"
                options={providerModels.map((pm) => {
                  const key = `${pm.providerId}:${pm.modelSlug}`;
                  const isDefault = pm.modelSlug === providers.find((p: any) => p.id === pm.providerId)?.defaultModel;
                  return {
                    value: key,
                    label: `${pm.providerName} / ${pm.modelSlug}${isDefault ? ' (default)' : ''}`,
                  };
                })}
                optionRender={(option) => {
                  const pm = providerModels.find((p) => `${p.providerId}:${p.modelSlug}` === option.value);
                  const providerType = pm?.providerType || '';
                  const color = providerType === 'OPENAI' ? '#52c41a' : providerType === 'ANTHROPIC' ? '#722ed1' : '#1890ff';
                  return (
                    <Space>
                      <Tag style={{ ...tagStyle(color), fontSize: 10, padding: '0 6px' }}>{providerType}</Tag>
                      <span>{option.label}</span>
                    </Space>
                  );
                }}
              />
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Input</Typography.Text>
            <Space style={{ marginBottom: 8 }}>
              <Button size="small" type={inputType === 'text' ? 'primary' : 'default'} onClick={() => setInputType('text')}>Text</Button>
              <Button size="small" type={inputType === 'image' ? 'primary' : 'default'} onClick={() => setInputType('image')}>Image</Button>
            </Space>
            {inputType === 'text' ? (
              <Input.TextArea rows={3} placeholder="Enter text input..." value={textInput} onChange={(e) => setTextInput(e.target.value)} />
            ) : (
              <>
                <Upload.Dragger accept="image/*" maxCount={1}
                  beforeUpload={(file) => { handleImageUpload(file); return false; }}
                  onRemove={() => setImageBase64(undefined)}>
                  <p><UploadOutlined style={{ fontSize: 24, color: token.colorTextSecondary }} /></p>
                  <p>Click or drag image (auto-resized)</p>
                </Upload.Dragger>
                {imageBase64 && (
                  <div style={{ marginTop: 8 }}>
                    <img src={`data:image/jpeg;base64,${imageBase64}`} alt="Preview"
                      style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, border: `1px solid ${token.colorBorderSecondary}` }} />
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch checked={enableThinking} onChange={setEnableThinking} />
            <div>
              <Typography.Text strong style={{ fontSize: 13 }}>Enable Thinking</Typography.Text>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Allows model to reason before answering. Increases latency but may improve quality.
              </Typography.Text>
            </div>
          </div>

          <Button type="primary" size="large" icon={<SendOutlined />} loading={running} onClick={handleRun} block>
            Run Comparison ({selectedModelKeys.length} model{selectedModelKeys.length !== 1 ? 's' : ''})
          </Button>
        </Card>

        {/* Results — Column Layout */}
        <Card title="Results" size="small">
          {running && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: token.colorTextSecondary }}>Running comparisons...</div>
            </div>
          )}
          {!running && results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: token.colorTextSecondary }}>
              Select a prompt and models, then click Run Comparison
            </div>
          )}
          {!running && results.length > 0 && (
            <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
              {results.map((run) => (
                <ColumnCard key={`${run.providerId}-${run.model}`} run={run} />
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
